import {
    Alert,
    Anchor,
    Badge,
    Center,
    Group,
    Loader,
    Paper,
    Select,
    Stack,
    Text,
    Title,
} from '@mantine/core'
import {format} from 'date-fns'
import {useMemo} from 'react'

import {MapErrorBoundary} from 'src/jobs/google-maps/map-error-boundary'
import {useMapLoadTracking} from 'src/jobs/google-maps/map-load-tracking'
import {parseApiDate} from 'src/jobs/job-schedule-format'

import classes from './run-map.module.css'
import {yardColor} from './run-map-colors'
import {RunMapGoogle} from './run-map-google'
import {
    collectWarnings,
    ESTIMATE_METHOD_LABEL,
    type EvaluatedRun,
    formatClockMinutes,
    formatDuration,
    formatMiles,
    ON_SITE_ASSUMPTION_LABEL,
    type RunMapDriver,
    type RunMapJob,
    type RunMapWarning,
    type RunMapYard,
    type RunTotals,
    yardColorIndex,
    yardPosition,
} from './run-map-logic'
import {RunMapSchematic} from './run-map-schematic'
import type {MapPin, MapYardMarker} from './run-map-surface'

export const ALL_DRIVERS = 'all'

function formatRunMapWarningTag(warning: RunMapWarning): string {
    if (warning.kind === 'late_leg') return 'Does not fit'

    return warning.kind === 'closer_yard' ? 'Yard' : 'Location'
}

export type RunMapMode = 'google' | 'schematic' | 'loading'

export type RunMapPanelProps = {
    days: string[]
    day: string
    today: string
    onDayChange: (day: string) => void
    drivers: RunMapDriver[]
    driverId: string | null
    onDriverChange: (driverId: string | null) => void
    jobs: RunMapJob[]
    yards: RunMapYard[]
    shownYardIds: string[]
    runs: EvaluatedRun[]
    mode: RunMapMode
    schematicNote: string | null
    locationsLoading?: boolean
    locationsError?: string | null
    onOpenJob: (jobId: string) => void
}

function formatRunMapDayLabel(day: string, today: string): string {
    const label = format(parseApiDate(day), 'EEE, MMM d')
    return day === today ? `${label} (today)` : label
}

export function methodLabel(method: RunTotals['method']): string | null {
    switch (method) {
        case 'google':
            return 'Drive times: Google Maps'
        case 'estimate':
            return `Drive times: ${ESTIMATE_METHOD_LABEL}`
        case 'mixed':
            return `Drive times: Google Maps where available, otherwise ${ESTIMATE_METHOD_LABEL}`
        default:
            return null
    }
}

function formatLegTimeLabel(leg: {miles: number; minutes: number; method: string}): string {
    return `${formatMiles(leg.miles)} · ${formatDuration(leg.minutes)}${
        leg.method === 'estimate' ? ' (approx.)' : ''
    }`
}

function formatJobStaffingLabel(job: RunMapJob): string {
    return job.staffing === 'staffed' ? 'fully staffed' : 'needs a driver'
}

function buildPinAriaLabel(job: RunMapJob, yardName: string | null, stopNumber: number | null) {
    const parts = [
        stopNumber != null ? `Stop ${stopNumber}` : null,
        `${job.jobCode} ${job.label}`,
        job.customer,
        job.startMinutes != null
            ? `starts ${formatClockMinutes(job.startMinutes)}`
            : 'no start time',
        formatJobStaffingLabel(job),
        yardName ? `${yardName} yard` : 'no yard',
    ]
    return `${parts.filter(Boolean).join(', ')}. Open job`
}

export function RunMapPanel({
    days,
    day,
    today,
    onDayChange,
    drivers,
    driverId,
    onDriverChange,
    jobs,
    yards,
    shownYardIds,
    runs,
    mode,
    schematicNote,
    locationsLoading = false,
    locationsError = null,
    onOpenJob,
}: RunMapPanelProps) {
    const tracking = useMapLoadTracking('dispatch_run_map')
    const selectedRun = useMemo(
        () => (driverId ? (runs.find((run) => run.driver.id === driverId) ?? null) : null),
        [driverId, runs],
    )
    const pins: MapPin[] = useMemo(() => {
        const stopNumbers = new Map(
            (selectedRun?.stops ?? []).map((stop) => [stop.job.id, stop.number]),
        )
        const offBoardStops = (selectedRun?.stops ?? [])
            .map((stop) => stop.job)
            .filter((job) => !jobs.some((shown) => shown.id === job.id))
        return [...jobs, ...offBoardStops].flatMap((job) => {
            if (job.position == null) {
                return []
            }

            const stopNumber = stopNumbers.get(job.id) ?? null
            return [
                {
                    job,
                    position: job.position,
                    color: yardColor(yardColorIndex(yards, job.yardId)),
                    stopNumber,
                    dimmed: selectedRun != null && stopNumber == null,
                    ariaLabel: buildPinAriaLabel(
                        job,
                        yards.find((yard) => yard.id === job.yardId)?.name ?? null,
                        stopNumber,
                    ),
                },
            ]
        })
    }, [jobs, selectedRun, yards])

    const yardMarkers: MapYardMarker[] = useMemo(
        () =>
            yards
                .filter((yard) => shownYardIds.includes(yard.id))
                .flatMap((yard) => {
                    const position = yardPosition(yard)
                    return position
                        ? [{yard, position, color: yardColor(yardColorIndex(yards, yard.id))}]
                        : []
                }),
        [shownYardIds, yards],
    )

    const warnings: RunMapWarning[] = useMemo(
        () => collectWarnings(jobs, selectedRun ? [selectedRun] : runs),
        [jobs, runs, selectedRun],
    )
    const legs = selectedRun?.legs ?? []
    const overallMethod: RunTotals['method'] = useMemo(() => {
        const methods = new Set(
            (selectedRun ? [selectedRun] : runs)
                .map((run) => run.totals.method)
                .filter((method) => method != null),
        )
        if (methods.size === 0) return null
        if (methods.size > 1 || methods.has('mixed')) return 'mixed'

        return [...methods][0] ?? null
    }, [runs, selectedRun])

    const driverOptions = [
        {value: ALL_DRIVERS, label: 'All drivers'},
        ...drivers.map((driver) => ({
            value: driver.id,
            label: `${driver.name} (${driver.jobCount} job${driver.jobCount === 1 ? '' : 's'})`,
        })),
    ]
    const yardIdsOnDay = [...new Set(jobs.map((job) => job.yardId))]
    const legendYards = yards.filter(
        (yard) => shownYardIds.includes(yard.id) || yardIdsOnDay.includes(yard.id),
    )

    const surfaceProps = {pins, yardMarkers, legs, onOpenJob}
    const fitKey = locationsLoading
        ? null
        : `${day}:${shownYardIds.join(',')}:${driverId ?? ALL_DRIVERS}`
    const schematic = (
        <>
            <RunMapSchematic {...surfaceProps} />
            {schematicNote ? (
                <Text size="sm" c="dimmed" px="sm" pb="xs" role="note">
                    {schematicNote}
                </Text>
            ) : null}
        </>
    )

    return (
        <Stack gap="sm" className={classes['root']} data-testid="run-map">
            <Group gap="sm" wrap="wrap" align="flex-end">
                <Select
                    label="Day"
                    aria-label="Map day"
                    data={days.map((value) => ({value, label: formatRunMapDayLabel(value, today)}))}
                    value={day}
                    onChange={(value) => value && onDayChange(value)}
                    allowDeselect={false}
                    w={180}
                />
                <Select
                    label="Driver"
                    aria-label="Driver run"
                    data={driverOptions}
                    value={driverId ?? ALL_DRIVERS}
                    onChange={(value) =>
                        onDriverChange(!value || value === ALL_DRIVERS ? null : value)
                    }
                    allowDeselect={false}
                    searchable
                    nothingFoundMessage="No driver booked"
                    w={240}
                />
                <Text size="sm" c="dimmed" aria-live="polite">
                    {jobs.length} job{jobs.length === 1 ? '' : 's'} ·{' '}
                    {jobs.filter((job) => job.staffing === 'needs_driver').length} need a driver
                </Text>
            </Group>

            {locationsError ? (
                <Alert color="red" title="Could not load job locations">
                    {locationsError}
                </Alert>
            ) : null}

            <div className={classes['layout']}>
                <Stack gap="xs" className={classes['mapArea']}>
                    {mode === 'google' ? (
                        <MapErrorBoundary onError={tracking.markRuntimeError} fallback={schematic}>
                            <RunMapGoogle {...surfaceProps} fitKey={fitKey} tracking={tracking} />
                            {locationsLoading ? (
                                <Center className={classes['mapLoading']}>
                                    <Loader aria-label="Loading run map" />
                                </Center>
                            ) : null}
                        </MapErrorBoundary>
                    ) : locationsLoading || mode === 'loading' ? (
                        <Center py={80}>
                            <Loader aria-label="Loading run map" />
                        </Center>
                    ) : (
                        schematic
                    )}
                </Stack>

                <Stack gap="sm" className={classes['side']}>
                    <Paper withBorder p="sm" radius="md">
                        <Title order={2} size="h5" mb={6}>
                            Key
                        </Title>
                        <Stack gap={4}>
                            {legendYards.map((yard) => (
                                <Group key={yard.id} gap={6} wrap="nowrap">
                                    <span
                                        className={classes['legendSwatch']}
                                        style={{
                                            borderColor: yardColor(yardColorIndex(yards, yard.id))
                                                .css,
                                            background: yardColor(yardColorIndex(yards, yard.id))
                                                .css,
                                        }}
                                        aria-hidden
                                    />
                                    <Text size="sm">{yard.name} jobs</Text>
                                </Group>
                            ))}
                            <Group gap={6} wrap="nowrap">
                                <span
                                    className={classes['legendSwatch']}
                                    style={{borderColor: 'var(--mantine-color-text)'}}
                                    aria-hidden
                                />
                                <Text size="sm">Hollow: needs a driver</Text>
                            </Group>
                            <Group gap={6} wrap="nowrap">
                                <span
                                    className={classes['legendSwatch']}
                                    style={{
                                        borderColor: 'var(--mantine-color-text)',
                                        background: 'var(--mantine-color-text)',
                                    }}
                                    aria-hidden
                                />
                                <Text size="sm">Filled: fully staffed</Text>
                            </Group>
                            <Group gap={6} wrap="nowrap">
                                <span
                                    className={classes['legendHome']}
                                    style={{background: 'var(--mantine-color-text)'}}
                                    aria-hidden
                                />
                                <Text size="sm">
                                    Yard (home)
                                    {yardMarkers.length === 0
                                        ? ', not mapped. Runs start at the first job'
                                        : ''}
                                </Text>
                            </Group>
                        </Stack>
                    </Paper>

                    {selectedRun ? (
                        <RunSummary run={selectedRun} onOpenJob={onOpenJob} />
                    ) : (
                        <DriversSummary runs={runs} onDriverChange={onDriverChange} />
                    )}

                    {methodLabel(overallMethod) ? (
                        <Text size="xs" c="dimmed" data-testid="run-map-method">
                            {methodLabel(overallMethod)}
                        </Text>
                    ) : null}

                    <Paper withBorder p="sm" radius="md">
                        <Title order={2} size="h5" mb={6}>
                            Warnings {warnings.length > 0 ? `(${warnings.length})` : ''}
                        </Title>
                        {warnings.length === 0 ? (
                            <Text size="sm" c="dimmed">
                                No warnings
                            </Text>
                        ) : (
                            <ul className={classes['legList']} aria-label="Run map warnings">
                                {warnings.map((warning) => (
                                    <li key={warning.key} className={classes['legItem']}>
                                        <Group gap={6} wrap="nowrap" align="flex-start">
                                            <Badge
                                                size="xs"
                                                variant="light"
                                                color={
                                                    warning.kind === 'late_leg' ? 'red' : 'yellow'
                                                }
                                                style={{flexShrink: 0, overflow: 'visible'}}
                                                title={formatRunMapWarningTag(warning)}
                                            >
                                                {formatRunMapWarningTag(warning)}
                                            </Badge>
                                            <Anchor
                                                component="button"
                                                type="button"
                                                size="sm"
                                                ta="left"
                                                onClick={() => onOpenJob(warning.jobId)}
                                            >
                                                {warning.message}
                                            </Anchor>
                                        </Group>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Paper>
                </Stack>
            </div>
            <Text size="xs" c="dimmed">
                Pins show each job's first location. Jobs with no start time run last.
            </Text>
        </Stack>
    )
}

function RunSummary({run, onOpenJob}: {run: EvaluatedRun; onOpenJob: (jobId: string) => void}) {
    const firstStop = run.stops[0]
    return (
        <Paper withBorder p="sm" radius="md" data-testid="run-map-run">
            <Title order={2} size="h5">
                {`${run.driver.name}'s run`}
            </Title>
            <Text size="xs" c="dimmed" mb={6}>
                In start-time order, {ON_SITE_ASSUMPTION_LABEL} at each job
            </Text>
            {run.stops.length === 0 ? (
                <Text size="sm" c="dimmed">
                    No mapped stops for this driver
                </Text>
            ) : (
                <ol className={classes['legList']} aria-label={`${run.driver.name}'s legs`}>
                    {run.origin == null && firstStop ? (
                        <li className={classes['legItem']}>
                            <Text size="sm">
                                Starts at{' '}
                                <Anchor
                                    component="button"
                                    type="button"
                                    size="sm"
                                    onClick={() => onOpenJob(firstStop.job.id)}
                                >
                                    1. {firstStop.job.jobCode}
                                </Anchor>
                            </Text>
                            <Text size="xs" c="dimmed">
                                {run.yard
                                    ? `${run.yard.name} has no map position`
                                    : 'No yard for this run'}
                            </Text>
                        </li>
                    ) : null}
                    {run.legs.map((leg) => {
                        const toStop = leg.toStop
                        return (
                            <li
                                key={`${leg.key}:${toStop?.number ?? 'yard'}`}
                                className={classes['legItem']}
                                data-testid="run-map-leg-item"
                            >
                                <Text size="sm">
                                    {leg.fromLabel} →{' '}
                                    {toStop ? (
                                        <>
                                            <Anchor
                                                component="button"
                                                type="button"
                                                size="sm"
                                                onClick={() => onOpenJob(toStop.job.id)}
                                            >
                                                {leg.toLabel}
                                            </Anchor>{' '}
                                            <Text span size="xs" c="dimmed">
                                                {toStop.job.startMinutes != null
                                                    ? `starts ${formatClockMinutes(toStop.job.startMinutes)}`
                                                    : 'no start time'}
                                            </Text>
                                        </>
                                    ) : (
                                        <>
                                            {leg.toLabel}{' '}
                                            <Text span size="xs" c="dimmed">
                                                {leg.arrivalMinutes != null
                                                    ? `back about ${formatClockMinutes(leg.arrivalMinutes)}`
                                                    : 'back to the yard'}
                                            </Text>
                                        </>
                                    )}
                                </Text>
                                <Group gap={6}>
                                    <Text size="sm" fw={500}>
                                        {formatLegTimeLabel(leg)}
                                    </Text>
                                    {leg.lateByMinutes != null ? (
                                        <Badge size="xs" color="red" variant="light">
                                            Does not fit · ~{formatDuration(leg.lateByMinutes)} late
                                        </Badge>
                                    ) : null}
                                </Group>
                            </li>
                        )
                    })}
                </ol>
            )}
            <Text size="sm" fw={600} mt={6} data-testid="run-map-totals">
                Day total: {formatMiles(run.totals.miles)} · {formatDuration(run.totals.minutes)}{' '}
                driving{run.totals.method === 'estimate' ? ' (approx.)' : ''}
            </Text>
            {run.unmappedJobs.length > 0 ? (
                <Text size="xs" c="dimmed" mt={4}>
                    Not on the map: {run.unmappedJobs.map((job) => job.jobCode).join(', ')} (no
                    usable location)
                </Text>
            ) : null}
        </Paper>
    )
}

function DriversSummary({
    runs,
    onDriverChange,
}: {
    runs: EvaluatedRun[]
    onDriverChange: (driverId: string | null) => void
}) {
    return (
        <Paper withBorder p="sm" radius="md">
            <Title order={2} size="h5" mb={6}>
                Drivers
            </Title>
            {runs.length === 0 ? (
                <Text size="sm" c="dimmed">
                    No drivers booked
                </Text>
            ) : (
                <ul className={classes['legList']} aria-label="Driver runs">
                    {runs.map((run) => {
                        const late = run.legs.filter((leg) => leg.lateByMinutes != null).length
                        return (
                            <li key={run.driver.id} className={classes['legItem']}>
                                <Group justify="space-between" gap={6} wrap="nowrap">
                                    <Anchor
                                        component="button"
                                        type="button"
                                        size="sm"
                                        onClick={() => onDriverChange(run.driver.id)}
                                        aria-label={`Show ${run.driver.name}'s run`}
                                    >
                                        {run.driver.name}
                                    </Anchor>
                                    <Text size="xs" c="dimmed">
                                        {run.stops.length} stop{run.stops.length === 1 ? '' : 's'} ·{' '}
                                        {formatMiles(run.totals.miles)} ·{' '}
                                        {formatDuration(run.totals.minutes)}
                                    </Text>
                                </Group>
                                {late > 0 ? (
                                    <Badge size="xs" color="red" variant="light">
                                        {late === 1
                                            ? '1 leg does not fit'
                                            : `${late} legs do not fit`}
                                    </Badge>
                                ) : null}
                            </li>
                        )
                    })}
                </ul>
            )}
        </Paper>
    )
}
